// SaveOrderTable.jsx
import React, { useMemo } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { itemFields } from "./formFields/asnTabsFields";

/**
 * SaveOrderTable
 * Props:
 *  - data: array
 *  - onEdit: fn(row)  (optional)
 *  - onDelete: fn(row) (optional)
 *  - onView: fn(row) (optional)
 *  - onViewSerials: fn(row) (optional) — open Serial/IMEI preview from SKU ID
 *  - readOnly: boolean (optional)
 */
function RowActionMenu({ params, onEditRow, onDeleteRow, onViewRow, readOnly }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const row = params?.row ?? {};

    if (readOnly) return null;
    if (!onEditRow && !onDeleteRow && !onViewRow) return null;

    return (
        <>
            <IconButton size="small" onClick={handleClick}>
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                {onEditRow && (
                    <MenuItem
                        onClick={() => {
                            onEditRow?.(row);
                            handleClose();
                        }}
                    >
                        <EditIcon fontSize="small" style={{ marginRight: 8 }} /> Edit
                    </MenuItem>
                )}
                {onDeleteRow && (
                    <MenuItem
                        onClick={() => {
                            // Pass full row so delete can use __groupKey / __index
                            onDeleteRow?.(row);
                            handleClose();
                        }}
                    >
                        <DeleteIcon fontSize="small" style={{ marginRight: 8 }} /> Remove
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

const actionsCol = (onEditRow, onDeleteRow, onViewRow, readOnly) => ({
    field: "actions",
    headerName: "Actions",
    width: 120,
    sortable: false,
    filterable: false,
    align: "left",
    renderCell: (params) => (
        <RowActionMenu
            params={params}
            onEditRow={onEditRow}
            onDeleteRow={onDeleteRow}
            onViewRow={onViewRow}
            readOnly={readOnly}
        />
    ),
});

const hasSerialPreview = (row) =>
    (Array.isArray(row?.serialDetails) && row.serialDetails.length > 0) ||
    !!row?.serialConfig?.inboundEnabled;

export default function LineItemsTable({
    data = [],
    onEdit,
    onDelete,
    onView,
    onViewSerials,
    readOnly = false,
}) {
    // Build columns from schema
    const itemColsFromSchema = useMemo(() => {
        const mapped = (itemFields || [])
            .filter((f) => {
                const label = (f.label || "").toLowerCase();
                const name = (f.name || "").toLowerCase();
                if (label === "item (sku fallback)") return false;
                if (label.includes("sku fallback")) return false;
                if (name === "item") return false;
                if (name === "skufallback" || name === "sku_fallback") return false;
                return true;
            })
            .map((f) => {
                const col = {
                    field: f.name,
                    headerName: f.label || f.name,
                    width: f.width || 140,
                };

                if (f.name === "skuId") {
                    col.renderCell = (params) => {
                        const row = params?.row ?? {};
                        const value = params.value ?? row.skuId ?? "";
                        if (onViewSerials && hasSerialPreview(row)) {
                            return (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-underline"
                                    style={{ fontSize: "inherit", lineHeight: "inherit" }}
                                    title="View Serial / IMEI details"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewSerials(row);
                                    }}
                                >
                                    {value}
                                </button>
                            );
                        }
                        return value;
                    };
                }

                return col;
            });

        return mapped;
    }, [onViewSerials]);

    // Ensure qcRequired column exists and renders Yes/No
    const withQcRequiredCol = useMemo(() => {
        const hasQc = itemColsFromSchema.some((c) => c.field === "qcRequired");
        if (hasQc) {
            return itemColsFromSchema.map((c) =>
                c.field === "qcRequired"
                    ? {
                        ...c,
                        renderCell: (params) =>
                            params.value === true || params.value === "Yes"
                                ? "Yes"
                                : params.value === false || params.value === "No"
                                    ? "No"
                                    : "",
                    }
                    : c
            );
        }
        return [
            ...itemColsFromSchema,
            {
                field: "qcRequired",
                headerName: "QC Required",
                width: 140,
                renderCell: (params) =>
                    params.value === true || params.value === "Yes"
                        ? "Yes"
                        : params.value === false || params.value === "No"
                            ? "No"
                            : "",
            },
        ];
    }, [itemColsFromSchema]);

    const ALL_ITEM_COLUMNS = useMemo(() => {
        return [actionsCol(onEdit, onDelete, onView, readOnly), ...withQcRequiredCol];
    }, [onEdit, onDelete, onView, readOnly, withQcRequiredCol]);

    const initialVisible = useMemo(() => {
        return ALL_ITEM_COLUMNS.map((c) => c.field);
    }, [ALL_ITEM_COLUMNS]);

    const initialRows = useMemo(
        () =>
            (data || []).map((row, idx) => {
                const qty =
                    row.orderQty ??
                    row.expectedQty ??
                    row.orderedQty ??
                    row.qty ??
                    row._raw?.detail?.orderedQty ??
                    0;
                const normalized = {
                    ...row,
                    id: row.id ?? row.lineNumber ?? `row-${idx + 1}`,
                    lineNumber:
                        row.lineNumber ??
                        row.lineNo ??
                        row._raw?.detail?.lineNumber ??
                        row.id ??
                        `row-${idx + 1}`,
                    skuId: row.skuId ?? row.item ?? row.sku ?? row._raw?.detail?.skuId ?? "",
                    description:
                        row.description ?? row.desc ?? row._raw?.detail?.description ?? "",
                    orderQty: qty,
                    expectedQty: row.expectedQty ?? qty,
                    pack: row.pack ?? row._raw?.detail?.pack ?? "STD",
                    uom: row.uom ?? row._raw?.detail?.uom ?? "EA",
                    itemweight: row.itemweight ?? row._raw?.detail?.itemWeight ?? "",
                    serialConfig: row.serialConfig ?? null,
                    serialDetails: Array.isArray(row.serialDetails) ? row.serialDetails : [],
                    __groupKey: row.__groupKey,
                    __index: row.__index,
                    qcRequired:
                        row.qcRequired === true
                            ? "Yes"
                            : row.qcRequired === false
                                ? "No"
                                : row.qcRequired === "Yes" || row.qcRequired === "No"
                                    ? row.qcRequired
                                    : "",
                };

                delete normalized.item;
                delete normalized.skuFallback;
                delete normalized.sku_fallback;

                return normalized;
            }),
        [data]
    );

    return (
        <div className="mt-4 mb-8">
            <CommonDataTable
                id="asn-blind-line-items-v3"
                allPossibleColumns={ALL_ITEM_COLUMNS}
                initialVisible={initialVisible}
                initialRows={initialRows}
                height={500}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 20]}
                forItems
                forItemsexport={true}
                checkboxSelection
            />
        </div>
    );
}
